import { defineStore } from "pinia";
import { ref, computed } from "vue";

const STORAGE_KEY = "car4u:favorites:v1";

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && typeof item === "object" && item.idInv != null);
  } catch (error) {
    console.error("Error al leer favoritos guardados:", error);
    return [];
  }
}

function writeStorage(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch (error) {
    console.error("Error al guardar favoritos:", error);
  }
}

export const useFavoritesStore = defineStore("favorites", () => {
  const items = ref(readStorage());

  const count = computed(() => items.value.length);
  const idSet = computed(() => new Set(items.value.map((i) => i.idInv)));

  const isFavorite = (idInv) => idSet.value.has(idInv);

  const buildEntry = (car) => ({
    idInv: car.idInv,
    marca: car.marca ?? "",
    modelo: car.modelo ?? "",
    version: car.version ?? "",
    anioModelo: car.anioModelo ?? "",
    foto: car.foto ?? "",
    precioContado: car.precioContado ?? 0,
    precioMensualidad: car.precioMensualidad ?? 0,
    preferencia: !!car.preferencia,
    transmision: car.transmision ?? "",
    kilometraje: car.kilometraje ?? 0,
    empresa: car.empresa ?? "",
    savedAt: Date.now(),
  });

  const add = (car) => {
    if (idSet.value.has(car.idInv)) return;
    items.value = [...items.value, buildEntry(car)];
    writeStorage(items.value);
  };

  const remove = (idInv) => {
    items.value = items.value.filter((i) => i.idInv !== idInv);
    writeStorage(items.value);
  };

  const toggle = (car) => {
    if (idSet.value.has(car.idInv)) remove(car.idInv);
    else add(car);
  };

  const clearAll = () => {
    items.value = [];
    writeStorage(items.value);
  };

  const updateEntry = (idInv, patch) => {
    items.value = items.value.map((i) => (i.idInv === idInv ? { ...i, ...patch } : i));
    writeStorage(items.value);
  };

  if (typeof window !== "undefined") {
    window.addEventListener("storage", (event) => {
      if (event.key === STORAGE_KEY) {
        items.value = readStorage();
      }
    });
  }

  return { items, count, isFavorite, add, remove, toggle, clearAll, updateEntry };
});
